import cx from 'classnames'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { SanityAccountShopListStrings } from '@data/sanity/queries/types/strings'
import { SanityUserShop } from '@data/sanity/queries/types/shop'
import { isEqual } from '@lib/helpers'
import { InstitutionContext } from '@lib/institution-context'
import { LanguageContext } from '@lib/language'
import { deactivateShop } from '@lib/print/request'
import { ShopItem } from '@lib/print/types'
import { getPageUrl, PageType } from '@lib/routes'
import { UserContext } from '@lib/user/context'

import Button from '@components/buttons/button'
import ComplexPortableText from '@components/portable-text/complex'
import SimpleLink from '@components/simple-link'

interface AccountShopListProps {
  accountStrings: SanityAccountShopListStrings
  className?: string
}

const AccountShopList = ({
  accountStrings,
  className,
}: AccountShopListProps) => {
  const { locale } = useContext(LanguageContext)
  const { institutions } = useContext(InstitutionContext)
  const { user, mutateUser } = useContext(UserContext)

  const router = useRouter()

  const [isLoading, setIsLoading] = useState(true)
  const [shops, setShops] = useState<SanityUserShop[]>([])
  const [deletedShopIds, setDeletedShopIds] = useState<string[]>([])

  const shopItems = useMemo(() => {
    return shops.map((shop) => {
      const institution = institutions.find(
        (institution) => institution.id === shop.institutionId,
      )
      const isActive = !deletedShopIds.includes(shop.id)
      const shopItem: ShopItem = {
        id: shop.id,
        slug: shop.slug,
        name: shop.name,
        institutionName: institution?.name,
        date: shop.date,
        isActive,
      }

      return shopItem
    })
  }, [deletedShopIds, institutions, shops])

  const handleEditClick = useCallback(
    (printShopSlug: string) => {
      const editPrintShopUrl = getPageUrl(
        PageType.EDIT_SHOP_PAGE,
        printShopSlug,
      )
      router.push(editPrintShopUrl)
    },
    [router],
  )

  const handleDeleteClick = useCallback(
    async (shopId: string) => {
      if (!user) {
        return
      }

      // Show confirmation
      const confirmResult = await confirm(
        accountStrings.accountShopListDeleteConfirmation,
      )

      if (!confirmResult) {
        return
      }

      // Add shop to deleted shop list
      setDeletedShopIds((currentIds) => [...currentIds, shopId])

      try {
        await deactivateShop(locale, {
          id: shopId,
        })

        // Remove shop from user shop list
        mutateUser({
          ...user,
          shops: user.shops?.filter((shop) => shop.id !== shopId),
        })
      } catch (error) {
        console.error(error)

        // Remove shop from deleted shop list
        setDeletedShopIds((currentIds) => [
          ...currentIds.filter((currentId) => currentId !== shopId),
        ])

        return
      }
    },
    [
      accountStrings.accountShopListDeleteConfirmation,
      locale,
      mutateUser,
      user,
    ],
  )

  // Wait for user to load
  useEffect(() => {
    if (!user) {
      return
    }

    if (isLoading) {
      setIsLoading(false)
    }
  }, [isLoading, user])

  // Load shops from user details
  useEffect(() => {
    if (!user?.shops || isLoading) {
      return
    }

    const newShops = user.shops ?? []

    if (!isEqual(shops, newShops)) {
      setShops(newShops)
    }
  }, [isLoading, shops, user?.shops])

  const dateFormat = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  const createShopUrl = getPageUrl(PageType.CREATE_SHOP_PAGE)

  return (
    <div className={className}>
      <h4 className="font-semibold">
        {accountStrings.accountShopListTeamListHeading}
      </h4>

      {shopItems.length === 0 && !isLoading && (
        <ComplexPortableText
          content={accountStrings.accountShopListTeamListEmpty}
          className="rc mt-3"
        />
      )}

      {shopItems.length > 0 && (
        <div className="mt-4">
          <div className="justify-evenly py-4 px-5 text-pageText text-sm hidden sm:flex">
            <div className="w-[30%] pr-3">
              {accountStrings.accountShopListName}
            </div>
            <div className="w-[30%] pr-3">
              {accountStrings.accountShopListSchool}
            </div>
            <div className="w-[20%] pr-3">
              {accountStrings.accountShopListDate}
            </div>
            <div className="w-[10%] pr-3"></div>
            <div className="w-[10%] pr-3"></div>
          </div>

          {shopItems.map((shopItem) => {
            const editPrintShopUrl = getPageUrl(
              PageType.EDIT_SHOP_PAGE,
              shopItem.slug,
              {
                parameters: {
                  step: 'review',
                },
              },
            )

            return (
              <SimpleLink
                key={shopItem.id}
                href={editPrintShopUrl}
                className={cx(
                  'flex flex-col sm:flex-row cursor-pointer py-5 px-5 border-t border-gray border-opacity-50 text-sm bg-gray-light bg-opacity-30 hover:bg-opacity-50',
                  {
                    'opacity-50': !shopItem.isActive,
                  },
                )}
              >
                <div className="sm:w-[30%] flex justify-between sm:block mb-3 sm:mb-0">
                  <div className="text-pageText sm:hidden">
                    {accountStrings.accountShopListName}
                  </div>
                  <div className="text-right sm:text-left sm:pr-3">
                    {shopItem.name}
                  </div>
                </div>

                <div className="sm:w-[30%] flex justify-between sm:block mb-3 sm:mb-0">
                  <div className="text-pageText sm:hidden">
                    {accountStrings.accountShopListSchool}
                  </div>
                  <div className="text-right sm:text-left sm:pr-3">
                    {shopItem.institutionName}
                  </div>
                </div>

                <div className="sm:w-[20%] flex justify-between sm:block mb-3 sm:mb-0">
                  <div className="text-pageText sm:hidden">
                    {accountStrings.accountShopListDate}
                  </div>
                  <div className="text-right sm:text-left sm:pr-3">
                    {dateFormat.format(new Date(shopItem.date))}
                  </div>
                </div>

                <div className="sm:w-[10%] flex justify-between sm:block mb-3 sm:mb-0 text-right">
                  <div className="text-pageText sm:hidden"></div>
                  <button
                    disabled={!shopItem.isActive}
                    onClick={(event) => {
                      event.preventDefault()
                      handleEditClick(shopItem.slug)
                    }}
                    className="text-left sm:pr-3"
                  >
                    {accountStrings.accountShopListEdit}
                  </button>
                </div>

                <div className="sm:w-[10%] flex justify-between sm:block mb-3 sm:mb-0 text-right">
                  <div className="text-pageText sm:hidden"></div>
                  <button
                    disabled={!shopItem.isActive}
                    onClick={(event) => {
                      event.preventDefault()
                      handleDeleteClick(shopItem.id)
                    }}
                    className="text-left sm:pr-3 text-red"
                  >
                    {accountStrings.accountShopListDelete}
                  </button>
                </div>
              </SimpleLink>
            )
          })}
        </div>
      )}

      <div className="mt-6 text-center">
        <NextLink href={createShopUrl}>
          <Button
            className="btn"
            role="link"
            tabIndex={0}
            variant="secondary"
            size="small"
            color="default"
            icon="Plus"
            iconSize="normal"
            iconAlignment="left"
            disabled={isLoading}
          >
            {accountStrings.accountShopListCreateTeamShop}
          </Button>
        </NextLink>
      </div>
    </div>
  )
}

export default AccountShopList
